import { booleanAttribute, Component, forwardRef, input, linkedSignal, model, output } from "@angular/core";
import { VdsIconComponent } from "@dcc/vestas-design-system/angular/components/icon";
import { BooleanInput } from "@angular/cdk/coercion";
import { ChangeFn, TouchFn } from "@dcc/vestas-design-system/angular/common";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

export type SwitchSize = "medium" | "large";

let uniqueId = 1;

export const VDS_SWITCH_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VdsSwitchComponent),
    multi: true
};

@Component({
    selector: "vds-switch",
    templateUrl: "./switch.component.html",
    styleUrl: "./switch.component.css",
    imports: [VdsIconComponent],
    providers: [VDS_SWITCH_CONTROL_VALUE_ACCESSOR]
})
export class VdsSwitchComponent implements ControlValueAccessor {
    readonly size = input<SwitchSize>("medium");

    readonly inputId = input<string>(`vds-switch-${uniqueId++}`);

    readonly disabled = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

    private disabledState = linkedSignal(this.disabled);

    checked = model<boolean>(false);

    readonly touched = output<void>();

    readonly toggleChange = output<boolean>();

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected onChange: ChangeFn<boolean> = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onTouched: TouchFn = () => {};

    toggle(): void {
        if (this.disabledState()) {
            return;
        }

        this.onTouched();
        this.touched.emit();

        this.checked.update((checked) => !checked);
        this.onChange(this.checked());
        this.toggleChange.emit(this.checked());
    }

    writeValue(value: boolean): void {
        this.checked.set(Boolean(value));
    }

    registerOnChange(fn: ChangeFn<boolean>): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: TouchFn): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabledState.set(isDisabled);
    }
}
